import type { FC } from 'react';
import React, { Fragment, memo } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { PERFORMANCE_SUBJECT_mainLayout } from '@confluence/performance/entry-points/constants.ts';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';

import type { MainLayoutComponentProps } from './MainLayoutComponent.tsx';
import { MainLayoutComponent } from './MainLayoutComponent.tsx';
import type {
	MainLayoutQuery$data,
	MainLayoutQuery$variables,
} from './__generated__/MainLayoutQuery.graphql.ts';

const MainLayoutQuery = graphql`
	query MainLayoutQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			lookAndFeel {
				content {
					screen {
						background
						backgroundColor
						backgroundImage
						backgroundPosition
						backgroundSize
						backgroundRepeat
						backgroundOrigin
						backgroundClip
						backgroundAttachment
						backgroundBlendMode
					}
				}
			}
		}
	}
`;

type MainLayoutProps = Pick<
	MainLayoutComponentProps,
	| 'children'
	| 'enableNavigation'
	| 'navView'
	| 'isViewPage'
	| 'isEdit'
	| 'editorPreloader'
	| 'fillContainer'
> & {
	spaceKey?: string;
};

export const MainLayout: FC<MainLayoutProps> = memo(
	({ spaceKey, isEdit, enableNavigation, isViewPage, ...otherProps }) => {
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
		const isMobileApp = isMobileApp_DO_NOT_USE();
		const isLivePage = useIsCurrentPageLive();

		const { data, error: unhandledError } = useQueryInterop<
			MainLayoutQuery$data,
			MainLayoutQuery$variables
		>(MainLayoutQuery, {
			variables: {
				// When spaceKey is undefined/falsy the query is skipped, so defaulting it to
				// an empty string doesn't change the logic but merely keeps TypeScript happy.
				spaceKey: spaceKey || '',
			},
			skip: !spaceKey || isEdit || isLivePage,
		});

		let error = unhandledError;
		if (isNotFoundError(error) || isUnauthorizedError(error)) {
			// The MainLayoutQuery only loads the space styling configuration, and neither error nor
			// missing configuration don't break the user experience. Unauthorized or NotFound error
			// (anonymous user, restricted or missing space/page) are handled by children components
			// of MainLayout and are part of the normal user flow, hence these can be safely muted.
			markErrorAsHandled(error);
			error = undefined;
		}

		return (
			<Fragment>
				{error && <ErrorDisplay error={error} />}
				<PerformanceStart subject={PERFORMANCE_SUBJECT_mainLayout} />
				<MainLayoutComponent
					{...otherProps}
					enableNavigation={isEmbeddedConfluence || isMobileApp ? false : enableNavigation}
					screen={data?.space?.lookAndFeel?.content?.screen ?? undefined}
					isViewPage={isViewPage}
					isEdit={isEdit}
				/>
			</Fragment>
		);
	},
);
